import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, Image, Dimensions } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListsManager from '../services/ListsManager';
import AsyncStorage from '../AsyncStorageShim';

const UserCard = ({ user }) => (
  <View style={styles.userCard}>
    <View style={styles.userAvatarContainer}>
      {user.profile_image_url ? (
        <Image
          source={{ uri: user.profile_image_url }}
          style={styles.userAvatar}
        />
      ) : (
        <View style={styles.placeholderAvatar}>
          <Text style={styles.placeholderText}>
            {user.name ? user.name.charAt(0).toUpperCase() : '?'}
          </Text>
        </View>
      )}
    </View>
    <Text style={styles.userName} numberOfLines={1}>{user.name || 'Unknown'}</Text>
    <Text style={styles.userHandle} numberOfLines={1}>@{user.username}</Text>
  </View>
);

const SuggestedListUsersScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { listDetails } = location.state || {};

  const [customListName, setCustomListName] = useState(listDetails?.list_name || '');
  const [customPrompt, setCustomPrompt] = useState(listDetails?.list_prompt || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  if (!listDetails) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigate(-1)} style={styles.backButton}>
            <Text style={styles.backButtonText}>← {t('app.lists.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('app.lists.suggestedList')}</Text>
        </View>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{t('app.lists.noListDetails')}</Text>
        </View>
      </View>
    );
  }

  const handleAddToMyLists = async () => {
    try {
      setIsProcessing(true);
      setError('');

      const success = await ListsManager.processSuggestedListOnBackend(
        listDetails.id,
        customListName || listDetails.list_name,
        customPrompt || listDetails.list_prompt
      );

      if (success) {
        navigate('/my-lists', { state: { refreshLists: true } });
      } else {
        setError('Failed to add list. Please try again.');
      }
    } catch (error) {
      console.error('Error adding suggested list:', error);
      setError('An error occurred while adding the list.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigate(-1)} style={styles.backButton}>
          <Text style={styles.backButtonText}>← {t('app.lists.back')}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('app.lists.suggestedList')}</Text>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {error ? <Text style={styles.errorText}>{error}</Text> : null}

        <View style={styles.listInfoContainer}>
          <Text style={styles.listNameText}>{listDetails.list_name}</Text>
          <Text style={styles.listPromptText}>{listDetails.list_prompt}</Text>
        </View>

        <Text style={styles.usersHeader}>{t('app.lists.usersInThisList')}</Text>

        <View style={styles.usersContainer}>
          {listDetails.users && listDetails.users.length > 0 ? (
            <View style={styles.usersGrid}>
              {listDetails.users.map((user, index) => (
                <UserCard key={index} user={user} />
              ))}
            </View>
          ) : (
            <View style={styles.noUsersContainer}>
              <Text style={styles.noUsersText}>{t('app.lists.noUsersInList')}</Text>
            </View>
          )}
        </View>
      </ScrollView>

      <View style={styles.fixedButtonContainer}>
        <TouchableOpacity
          style={[styles.addButton, isProcessing && styles.addButtonDisabled]}
          onPress={handleAddToMyLists}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <ActivityIndicator color="white" size="small" />
          ) : (
            <Text style={styles.addButtonText}>{t('app.lists.addToMyLists')}</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const { width } = Dimensions.get('window');
const cardWidth = (width - 64) / 3; // 3 cards per row with spacing

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'var(--bg-deep-void)',
    position: 'relative',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100,
  },
  header: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.08)',
    backgroundColor: 'var(--bg-card)',
    zIndex: 10,
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
  },
  backButton: {
    marginRight: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    transition: 'all 0.2s',
  },
  backButtonText: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '600',
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 20,
    fontWeight: '700',
    flex: 1,
    letterSpacing: '-0.01em',
    fontFamily: 'Outfit, sans-serif',
  },
  errorText: {
    color: 'var(--accent-warning)',
    backgroundColor: 'rgba(244, 63, 94, 0.1)',
    marginHorizontal: 20,
    marginTop: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(244, 63, 94, 0.2)',
    padding: 16,
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  listInfoContainer: {
    padding: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    margin: 20,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: 'inset 0 4px 20px rgba(0, 0, 0, 0.2)',
    alignItems: 'flex-start',
  },
  listNameText: {
    color: 'var(--text-primary)',
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 12,
    letterSpacing: '-0.02em',
    textAlign: 'left',
    alignSelf: 'flex-start',
    width: '100%',
    fontFamily: 'Outfit, sans-serif',
  },
  listPromptText: {
    color: 'var(--text-secondary)',
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'left',
    alignSelf: 'flex-start',
    width: '100%',
  },
  usersHeader: {
    color: 'var(--text-primary)',
    fontSize: 20,
    fontWeight: '700',
    marginHorizontal: 20,
    marginTop: 8,
    marginBottom: 16,
    fontFamily: 'Outfit, sans-serif',
  },
  usersContainer: {
    paddingHorizontal: 12,
    marginBottom: 20,
  },
  usersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  userCard: {
    width: cardWidth,
    marginHorizontal: 4,
    marginBottom: 16,
    padding: 16,
    backgroundColor: 'var(--bg-card)',
    borderRadius: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.2s',
  },
  userAvatarContainer: {
    marginBottom: 12,
    position: 'relative',
  },
  userAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    borderWidth: 2,
    borderColor: 'white',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  },
  placeholderAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'rgba(59, 130, 246, 0.4)',
  },
  placeholderText: {
    color: 'var(--text-primary)',
    fontSize: 24,
    fontWeight: '700',
    fontFamily: 'Outfit, sans-serif',
  },
  userName: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 4,
    width: '100%',
  },
  userHandle: {
    color: 'var(--text-muted)',
    fontSize: 12,
    textAlign: 'center',
    width: '100%',
  },
  noUsersContainer: {
    padding: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.05)',
    marginHorizontal: 8,
    borderStyle: 'dashed',
  },
  noUsersText: {
    color: 'var(--text-muted)',
    fontSize: 16,
    textAlign: 'center',
    fontWeight: '500',
  },
  fixedButtonContainer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 24,
    backgroundColor: 'var(--bg-card)',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    zIndex: 1000,
  },
  addButton: {
    backgroundColor: 'var(--accent-primary)',
    paddingVertical: 18,
    borderRadius: 16,
    alignItems: 'center',
    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)',
    transition: 'transform 0.1s',
  },
  addButtonDisabled: {
    backgroundColor: 'rgba(59, 130, 246, 0.3)',
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
  addButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: '0.02em',
  },
});

export default SuggestedListUsersScreen; 